import { memo, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { get as _get } from "lodash";
import {
  Alert,
  Box,
  Card,
  Button,
  Collapse,
  Link,
  CardHeader,
  CardContent,
  Typography,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BigNumber from "bignumber.js";
import { useWallet } from "../../hooks/useWallet";
import { useExplorer } from "../../hooks/useExplorer";
import { liquidityPosition } from "../../services/LiquidityPositionController";

// components mui

// icons

// components
import Maintenance from "../../components/Maintenance";
import PairLogos from "../../components/PairLogos";
import DetailRow from "../../components/DetailRow";

// Actions
import { setModal } from "../../redux/actions/modals";

// contracts
import { PeripheryContract, CoreContract } from "../../helpers/contracts";

// helpers
import { calculateAmount, calculatePercent, orderTokens } from "../../helpers/calculate";

// utils
import { loadTokenData } from "../../utils/token";
import { mapSettledWithLimit } from "../../utils/async";

// constants
import { CHAINS_IDS } from "../../constants/chains";
import { CONFIG_BASE } from "../../constants/configs";
import { ConnectButton } from "../../components/Buttons/ConnectButton";
import { formatAmount } from "../../utils/format";
import { explorerUrl } from "../../utils/explorer";

const POSITIONS_STALE_TIME = 30 * 1000;
const API_POSITIONS_REFRESH = 60 * 1000;

/**
 * Positions of a user from the KoinDX API (each with its pair and tokens), with their share of each reserve,
 * sorted by USD value
 */
const buildApiPositions = (positions, tokenList) =>
  positions
    .map((pos) => {
      // only pairs of the KoinDX periphery
      const pair = pos.pair;
      if (!pair || pair.protocolId !== CONFIG_BASE.contracts.periphery.address) return null;
      const listToken0 = tokenList.find((el) => el.address === pair.token0.id);
      const listToken1 = tokenList.find((el) => el.address === pair.token1.id);
      // the token list spells the symbols as the tokens do ("KOIN"; the API returns "koin")
      const token0 = {
        ...pair.token0,
        symbol: _get(listToken0, "symbol", pair.token0.symbol),
        logoURI: _get(listToken0, "logoURI", null),
      };
      const token1 = {
        ...pair.token1,
        symbol: _get(listToken1, "symbol", pair.token1.symbol),
        logoURI: _get(listToken1, "logoURI", null),
      };
      const totalPoolSupply = new BigNumber(pair.totalSupply);
      const userTotalLiquidityTokenPosition = new BigNumber(pos.liquidityTokenBalance);
      const reserve0 = new BigNumber(pair.reserve0);
      const reserve1 = new BigNumber(pair.reserve1);
      return {
        pairId: pair.id,
        userTotalLiquidityTokenPosition,
        // share of the pool in percent, its USD value and the user's part of each reserve
        poolShare: userTotalLiquidityTokenPosition.multipliedBy(100).dividedBy(totalPoolSupply),
        usdShare: userTotalLiquidityTokenPosition.multipliedBy(pair.reserveUSD).dividedBy(totalPoolSupply),
        totalPoolSupply,
        token0,
        reserve0,
        token0Price: new BigNumber(pair.token0Price),
        token1,
        reserve1,
        token1Price: new BigNumber(pair.token1Price),
        token0liquidity: userTotalLiquidityTokenPosition.multipliedBy(reserve0).dividedBy(totalPoolSupply),
        token1liquidity: userTotalLiquidityTokenPosition.multipliedBy(reserve1).dividedBy(totalPoolSupply),
      };
    })
    .filter(Boolean)
    .sort((p1, p2) => p2.usdShare.comparedTo(p1.usdShare));

// columns of the positions table: pool, LP tokens (desktop), value, expand icon
const POSITION_COLUMNS = { xs: "minmax(0, 1fr) auto 24px", md: "minmax(0, 1fr) 180px 140px 24px" };
// columns of the on-chain positions table: pool, share of pool, expand icon
const CHAIN_POSITION_COLUMNS = "minmax(0, 1fr) auto 24px";

// the clickable header of a position row
const ROW_BUTTON_SX = {
  width: "100%",
  display: "grid",
  alignItems: "center",
  gap: 2,
  px: 2.5,
  py: 2,
  border: 0,
  background: "none",
  color: "inherit",
  font: "inherit",
  textAlign: "left",
  cursor: "pointer",
  "&:hover": { backgroundColor: "background.light" },
  "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: "-2px" },
};

// the details of an open row: padding (not margins, so Collapse measures it) keeps the panel apart from the
// header hover, and the panel is recessed (page colour) so it never reads as part of that hover
const ROW_DETAILS_OUTER_SX = { px: 2.5, pt: 1, pb: 2.5 };
const ROW_DETAILS_SX = {
  p: 2,
  borderRadius: "12px",
  border: 1,
  borderColor: "divider",
  backgroundColor: "background.default",
};

const Stat = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 560, overflowWrap: "anywhere" }}>{children}</Typography>
  </Box>
);

// one position from the API; memoized so a refetch that returns the same data does not render it again
const PositionRow = memo(function PositionRow({ el }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const symbol0 = _get(el, "token0.symbol", "?");
  const symbol1 = _get(el, "token1.symbol", "?");
  const pairPath = `${_get(el, "token0.id", "")}/${_get(el, "token1.id", "")}`;
  const explorer = useExplorer();

  return (
    <Box component="li" sx={{ listStyle: "none", borderTop: 1, borderColor: "divider" }}>
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        sx={{ ...ROW_BUTTON_SX, gridTemplateColumns: POSITION_COLUMNS }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <PairLogos tokens={[el.token0, el.token1]} />
          <Typography noWrap sx={{ fontWeight: 600, fontSize: "1rem" }}>
            {symbol0} / {symbol1}
          </Typography>
        </Box>
        <Typography sx={{ display: { xs: "none", md: "block" }, textAlign: "right" }}>
          {formatAmount(el.userTotalLiquidityTokenPosition, { maxDecimals: 6 })}
        </Typography>
        <Typography sx={{ textAlign: "right", fontWeight: 600 }}>
          ${formatAmount(el.usdShare, { maxDecimals: 2 })}
        </Typography>
        <ExpandMoreIcon
          aria-hidden
          sx={{
            color: "text.secondary",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 150ms ease",
          }}
        />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={ROW_DETAILS_OUTER_SX}>
          <Box
            sx={{
              ...ROW_DETAILS_SX,
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" },
            }}
          >
            <Stat label="Share of pool">{formatAmount(el.poolShare, { maxDecimals: 2 })}%</Stat>
            <Stat label={`Your ${symbol0}`}>
              {formatAmount(el.token0liquidity, { maxDecimals: el.token0.decimals })}
            </Stat>
            <Stat label={`Your ${symbol1}`}>
              {formatAmount(el.token1liquidity, { maxDecimals: el.token1.decimals })}
            </Stat>
            <Stat label="Your LP tokens">{formatAmount(el.userTotalLiquidityTokenPosition, { maxDecimals: 8 })}</Stat>
            <Stat label={`Pool ${symbol0}`}>{formatAmount(el.reserve0, { maxDecimals: el.token0.decimals })}</Stat>
            <Stat label={`Pool ${symbol1}`}>{formatAmount(el.reserve1, { maxDecimals: el.token1.decimals })}</Stat>
            <Stat label="Pool LP supply">{formatAmount(el.totalPoolSupply, { maxDecimals: 8 })}</Stat>
            <Box sx={{ gridColumn: "1 / -1", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
              <Button variant="contained" size="small" onClick={() => navigate(`/liquidity/add/${pairPath}`)}>
                Add liquidity
              </Button>
              <Button variant="outlined" size="small" onClick={() => navigate(`/liquidity/remove/${pairPath}`)}>
                Remove liquidity
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Link
                href={explorerUrl(explorer, "address", el.pairId)}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                variant="body2"
              >
                View pool on {explorer.name}
              </Link>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
});

// a card with the page title and a short state: not connected, loading, failed
const LiquidityMessage = ({ children }) => (
  <Card sx={{ maxWidth: 480, mx: "auto" }}>
    <CardHeader title="Your liquidity" />
    <CardContent>{children}</CardContent>
  </Card>
);

// one imported pair read on-chain
const ChainPositionRow = ({ position }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const symbolA = _get(position, "tokenA.symbol", "");
  const symbolB = _get(position, "tokenB.symbol", "");

  return (
    <Box component="li" sx={{ listStyle: "none", borderTop: 1, borderColor: "divider" }}>
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        sx={{ ...ROW_BUTTON_SX, gridTemplateColumns: CHAIN_POSITION_COLUMNS }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <PairLogos tokens={[position.tokenA, position.tokenB]} />
          <Typography noWrap sx={{ fontWeight: 600, fontSize: "1rem" }}>
            {symbolA} / {symbolB}
          </Typography>
        </Box>
        <Typography sx={{ textAlign: "right" }}>{calculatePercent(position)}%</Typography>
        <ExpandMoreIcon
          aria-hidden
          sx={{
            color: "text.secondary",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 150ms ease",
          }}
        />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={ROW_DETAILS_OUTER_SX}>
          <Box sx={{ ...ROW_DETAILS_SX, display: "flex", flexDirection: "column", gap: 1 }}>
            <DetailRow
              label={`Pooled ${symbolA}`}
              value={formatAmount(calculateAmount(position, "A"), {
                maxDecimals: _get(position, "tokenA.decimals", 8),
              })}
            />
            <DetailRow
              label={`Pooled ${symbolB}`}
              value={formatAmount(calculateAmount(position, "B"), {
                maxDecimals: _get(position, "tokenB.decimals", 8),
              })}
            />
            <DetailRow label="Share of pool" value={`${calculatePercent(position)}%`} />
            <Box sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  navigate(
                    `/liquidity/remove/${_get(position, "tokenA.address", "")}/${_get(position, "tokenB.address", "")}`
                  )
                }
              >
                Remove liquidity
              </Button>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

// positions of imported pairs read on-chain ("Use KoinDX API" off)
const ChainPositions = ({ hasWallet, loading, positions, unsupported, onImport }) => {
  const navigate = useNavigate();
  if (!hasWallet) {
    return (
      <LiquidityMessage>
        <Typography sx={{ color: "text.secondary", mb: 2 }}>
          Connect your wallet to see your positions or add liquidity.
        </Typography>
        <ConnectButton />
      </LiquidityMessage>
    );
  }
  return (
    <Box sx={{ maxWidth: 720, mx: "auto", pb: { xs: 10, md: 6 } }}>
      <Card>
        <CardHeader
          title="Your liquidity"
          sx={{ alignItems: "center" }}
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={unsupported}
                aria-label="import position"
                onClick={onImport}
              >
                Import
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={unsupported}
                aria-label="add liquidity"
                onClick={() => navigate("/liquidity/add")}
              >
                Add liquidity
              </Button>
            </Box>
          }
        />
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, pt: 1, pb: 3 }}>
            <CircularProgress size={18} />
            <Typography sx={{ color: "text.secondary" }}>Loading your positions…</Typography>
          </Box>
        ) : positions.length === 0 ? (
          <Typography sx={{ color: "text.secondary", px: 2.5, pt: 1, pb: 3 }}>
            No positions yet. Import a pool you already provide liquidity to, or add liquidity to a new one.
          </Typography>
        ) : (
          <>
            <Box
              aria-hidden
              sx={{ display: "grid", gridTemplateColumns: CHAIN_POSITION_COLUMNS, gap: 2, px: 2.5, pt: 1, pb: 1.5 }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Pool
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "right" }}>
                Share of pool
              </Typography>
            </Box>
            <Box component="ul" aria-label="Your liquidity positions" sx={{ m: 0, p: 0 }}>
              {positions.map((position, index) => (
                <ChainPositionRow position={position} key={`position-${index}`} />
              ))}
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
};

// positions from the KoinDX API; a thin bar shows a background refresh
const ApiPositions = ({ positions, refreshing }) => {
  const navigate = useNavigate();
  const addButton = (
    <Button variant="contained" size="small" onClick={() => navigate("/liquidity/add")}>
      Add liquidity
    </Button>
  );
  return (
    <Box sx={{ maxWidth: 960, mx: "auto", pb: { xs: 10, md: 6 } }}>
      <Card>
        <CardHeader title="Your liquidity" action={positions.length ? addButton : null} sx={{ alignItems: "center" }} />
        <LinearProgress
          aria-label="refreshing positions"
          sx={{ height: "2px", mx: 2.5, visibility: refreshing ? "visible" : "hidden" }}
        />
        {positions.length === 0 ? (
          <Box sx={{ px: 2.5, pt: 1, pb: 3 }}>
            <Typography sx={{ color: "text.secondary", mb: 2 }}>This wallet has no liquidity positions yet.</Typography>
            {addButton}
          </Box>
        ) : (
          <>
            <Box
              aria-hidden
              sx={{ display: "grid", gridTemplateColumns: POSITION_COLUMNS, gap: 2, px: 2.5, pt: 1, pb: 1.5 }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Pool
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", textAlign: "right", display: { xs: "none", md: "block" } }}
              >
                LP tokens
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "right" }}>
                Value
              </Typography>
            </Box>
            <Box component="ul" aria-label="Your liquidity positions" sx={{ m: 0, p: 0 }}>
              {positions.map((el) => (
                <PositionRow el={el} key={el.pairId} />
              ))}
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
};

const Liquidity = () => {
  // Hooks
  const dispatch = useDispatch();

  // selectors
  const networkSelector = useSelector((state) => state.settings.network);
  const useAPISelector = useSelector((state) => state.settings.useapi);
  const walletSelector = useWallet();
  const base_tokens = useSelector((state) => state.tokens.base_tokens);
  const custom_tokens = useSelector((state) => state.tokens.custom_tokens);
  const liquidityPositions = useSelector((state) => state.liquidity.positions);

  // variables
  const provider = _get(walletSelector, "provider", null);
  const signer = _get(walletSelector, "signer", null);
  const address = _get(walletSelector, "wallet[0].address", null);
  const tokenList = useMemo(() => base_tokens.concat(custom_tokens), [base_tokens, custom_tokens]);

  // functions
  const importPosition = () => {
    dispatch(setModal("ImportPosition"));
  };

  // positions of the imported pairs, read on-chain in parallel
  const loadChainPositions = async () => {
    const functions = PeripheryContract(provider, signer).functions;
    const findToken = async (tokenAddress) =>
      tokenList.find((token) => token.address === tokenAddress) || loadTokenData(tokenAddress);
    const results = await mapSettledWithLimit(liquidityPositions, 4, async (position) => {
      try {
        let _tokens = orderTokens({ address: _get(position, "tokenA", "") }, { address: _get(position, "tokenB", "") });
        let pairAddress = (
          await functions.get_pair({
            tokenA: _get(_tokens, "token0.address", ""),
            tokenB: _get(_tokens, "token1.address", ""),
          })
        ).result;
        if (!pairAddress) return null;
        let pairContract = CoreContract(pairAddress.value, provider, signer);
        let balance = (await pairContract.functions.balance_of({ owner: address })).result;
        if (_get(balance, "value", "0") == "0") return null;
        let [supply, reserves, tokenAData, tokenBData] = await Promise.all([
          pairContract.functions.total_supply().then(({ result }) => result),
          pairContract.functions.get_reserves().then(({ result }) => result),
          findToken(_get(_tokens, "token0.address", "")),
          findToken(_get(_tokens, "token1.address", "")),
        ]);
        return {
          owner: address,
          totalSupply: _get(supply, "value", "0"),
          shares: _get(balance, "value", "0"),
          reserves: reserves,
          tokenA: tokenAData,
          tokenB: tokenBData,
        };
      } catch (error) {
        console.log(error);
        return null;
      }
    });
    return results.map((result) => result.value).filter(Boolean);
  };

  // positions from the KoinDX API, each with its pair and tokens (one request)
  const loadApiPositions = () => liquidityPosition.getUserPositions(address);

  // queries, keyed by wallet: the positions of a previous wallet are never shown
  // the API positions refresh every minute while the tab is visible; going back to the page within
  // 30 s reuses them, later the cached ones show at once and refresh in the background
  const apiQuery = useQuery({
    queryKey: ["liquidityPositions", "api", address],
    queryFn: loadApiPositions,
    enabled: Boolean(address) && Boolean(useAPISelector),
    staleTime: POSITIONS_STALE_TIME,
    refetchInterval: API_POSITIONS_REFRESH,
  });
  // on-chain positions cost several RPC calls each, they refresh on visits and after transactions only
  const chainQuery = useQuery({
    queryKey: ["liquidityPositions", "chain", address, liquidityPositions],
    queryFn: loadChainPositions,
    enabled: Boolean(address) && !useAPISelector && networkSelector != CHAINS_IDS.UNSUPPORTED,
    staleTime: POSITIONS_STALE_TIME,
  });
  const apiPositions = useMemo(
    () => (apiQuery.data ? buildApiPositions(apiQuery.data, tokenList) : []),
    [apiQuery.data, tokenList]
  );
  const positions = chainQuery.data || [];
  const loading = useAPISelector ? apiQuery.isLoading : chainQuery.isLoading;
  // a failed background refresh keeps showing the positions already loaded
  const apiError = apiQuery.isError && !apiQuery.data;

  if (!_get(CONFIG_BASE, "contracts.periphery.launched", false)) {
    return <Maintenance />;
  }

  return (
    <Box sx={{ mb: { xs: 10, md: 2.5 } }}>
      {useAPISelector ? (
        !_get(walletSelector, "wallet", null) ? (
          <LiquidityMessage>
            <Typography sx={{ color: "text.secondary", mb: 2 }}>
              Connect your wallet to see your liquidity positions.
            </Typography>
            <ConnectButton />
          </LiquidityMessage>
        ) : !loading ? (
          apiError ? (
            <LiquidityMessage>
              <Alert severity="error" sx={{ mb: 2 }}>
                Could not load your positions from the KoinDX API. Try again, or turn off &quot;Use KoinDX API&quot; in
                Settings.
              </Alert>
              <Button variant="outlined" onClick={() => apiQuery.refetch()} disabled={apiQuery.isFetching}>
                {apiQuery.isFetching ? "Retrying…" : "Retry"}
              </Button>
            </LiquidityMessage>
          ) : (
            <ApiPositions positions={apiPositions} refreshing={apiQuery.isFetching} />
          )
        ) : (
          <LiquidityMessage>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CircularProgress size={18} />
              <Typography sx={{ color: "text.secondary" }}>Loading your positions…</Typography>
            </Box>
          </LiquidityMessage>
        )
      ) : (
        <ChainPositions
          hasWallet={Boolean(_get(walletSelector, "wallet", null))}
          loading={loading}
          positions={positions}
          unsupported={networkSelector == CHAINS_IDS.UNSUPPORTED}
          onImport={importPosition}
        />
      )}
    </Box>
  );
};

export default Liquidity;
